import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { dietService } from '../../services/dietService';
import { Diet, CreateDietRequest, UpdateDietRequest, DietType } from '../../types/diet';
import Button from '../UI/Button';
import { useNavigate } from 'react-router-dom';


interface DietModalProps {
  diet?: Diet | null;
  onClose: () => void;
  onSuccess: () => void;
}

const DietModal: React.FC<DietModalProps> = ({
  diet,
  onClose,
  onSuccess,
}) => {
  const [error, setError] = useState<string | null>(null);
  // UI state for Menu selection (optional link after creation)
  const navigate = useNavigate();

  const [selectedMenuId, setSelectedMenuId] = useState<string>('');
  const isEditing = !!diet;

  // Load menus for select
  const { data: menusData } = useQuery({
    queryKey: ['menus', 'for-select'],
    queryFn: () => dietService.getMenus({ limit: 1000 }),
  });

  // Load diet details when editing to prefill types
  const { data: dietDetail } = useQuery({
    queryKey: ['diet', diet?.id],
    enabled: isEditing && !!diet?.id,
    queryFn: () => dietService.getDiet(diet!.id),
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
    setValue,
  } = useForm<CreateDietRequest>({
    defaultValues: {
      name: diet?.name || '',
      code: diet?.code || '',
      description: diet?.description || '',
      periodWeeks: diet?.periodWeeks || undefined,
      startingDay: (diet?.startingDay as any) || 'MONDAY',
      startingDate: diet?.startingDate ? (diet.startingDate as any).toString().split('T')[0] : '',
      status: diet?.status || 'ACTIVE',
      types: [] as DietType[],
    },
  });

  useEffect(() => {
    if (diet) {
      reset({
        name: diet.name,
        code: diet.code || '',
        description: diet.description || '',
        periodWeeks: diet.periodWeeks,
        startingDay: (diet.startingDay as any) || 'MONDAY',
        startingDate: diet.startingDate ? (diet.startingDate as any).toString().split('T')[0] : '',
        status: diet.status,
      });
    }
  }, [diet, reset]);

  // When editing, preselect the first menu already linked to this diet (if any)
  useEffect(() => {
    if (isEditing && diet && menusData?.menus) {
      const assigned = menusData.menus.find((m) => m.dietId === diet.id);
      setSelectedMenuId(assigned?.id || '');
    }
  }, [isEditing, diet, menusData]);

  const DIET_TYPE_OPTIONS: { value: DietType; label: string }[] = [
    { value: 'PER_DAY' as DietType, label: 'Per Day' },
    { value: 'PER_MEAL' as DietType, label: 'Per Meal' },
    { value: 'HALF_BOARD_AM' as DietType, label: 'Half Board AM' },
    { value: 'LUNCH_BOX' as DietType, label: 'Lunch Box' },
    { value: 'HALF_BOARD_PM' as DietType, label: 'Half Board PM' },
  ];

  // Prefill types on edit from diet details
  useEffect(() => {
    if (isEditing && dietDetail?.diet?.dietTypes) {
      const values = ((dietDetail.diet.dietTypes || []).map((dt: any) => dt.type) as DietType[]);
      setValue('types', values);
    }
  }, [isEditing, dietDetail, setValue]);


  const createMutation = useMutation({
    mutationFn: (data: CreateDietRequest) => dietService.createDiet(data),
    onSuccess: async (res) => {
      try {
        if (selectedMenuId && (res as any)?.diet?.id) {
          await dietService.updateMenu(selectedMenuId, { dietId: (res as any).diet.id });
        }
      } finally {
        onSuccess();
      }
    },
    onError: (error: any) => {
      setError(error.response?.data?.error || 'Failed to create diet');
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: UpdateDietRequest) =>
      dietService.updateDiet(diet!.id, data),
    onSuccess: () => {
      onSuccess();
    },
    onError: (error: any) => {
      setError(error.response?.data?.error || 'Failed to update diet');
    },
  });

  const onSubmit = (data: CreateDietRequest) => {
    setError(null);

    if (isEditing) {
      updateMutation.mutate(data as UpdateDietRequest, {
        onSuccess: async () => {
          try {
            if (selectedMenuId && diet?.id) {
              await dietService.updateMenu(selectedMenuId, { dietId: diet.id });
            }
          } finally {
            onSuccess();
          }
        },
        onError: (error: any) => setError(error.response?.data?.error || 'Failed to update diet'),
      });
    } else {
      createMutation.mutate(data);
    }
  };

  const isLoading = createMutation.isPending || updateMutation.isPending;

  return (
    <Transition appear show={true} as={React.Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={React.Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={React.Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-lg bg-white p-6 text-left align-middle shadow-xl transition-all">
                <div className="flex items-center justify-between mb-4">
                  <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-gray-900">
                    {isEditing ? 'Edit Diet' : 'Create New Diet'}
                  </Dialog.Title>
                  <button
                    onClick={onClose}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>
                {error && (
                  <div className="mb-4 rounded-lg bg-red-50 p-4">
                    <div className="text-sm text-red-700">{error}</div>
                  </div>
                )}

                <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                  <div>
                    <label htmlFor="name" className="form-label">
                      Name *
                    </label>
                    <input
                      {...register('name', {
                        required: 'Name is required',
                        maxLength: {
                          value: 100,
                          message: 'Name must be less than 100 characters',
                        },
                      })}
                      type="text"
                      className="form-input"
                      placeholder="Enter diet name"
                    />
                    {errors.name && (
                      <p className="form-error">{errors.name.message}</p>
                    )}

                  </div>

                  <div>
                    <label htmlFor="description" className="form-label">
                      Description
                    </label>
                    <textarea
                      {...register('description')}
                      className="form-input"
                      rows={3}
                      placeholder="Enter diet description (optional)"
                    />
                    {errors.description && (
                      <p className="form-error">{errors.description.message}</p>
                    )}
                  </div>

                  <div>
                    <label htmlFor="code" className="form-label">Code *</label>
                    <input
                      {...register('code', {
                        required: 'Code is required',
                        maxLength: { value: 50, message: 'Code must be less than 50 characters' },
                      })}
                      type="text"
                      className="form-input"
                      placeholder="Enter diet code"
                    />
                    {(errors as any)?.code && (
                      <p className="form-error">{(errors as any).code.message as string}</p>
                    )}
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div>
                      <label className="form-label">Menu *</label>
                      <select
                        className="form-input"
                        value={selectedMenuId}
                        onChange={(e) => setSelectedMenuId(e.target.value)}
                      >
                        <option value="">Select</option>
                        {(menusData?.menus || []).map((m) => {
                          const assignedToOtherDiet = !!m.dietId && (!diet || m.dietId !== diet.id);
                          return (
                            <option key={m.id} value={m.id} disabled={assignedToOtherDiet}>
                              {m.name}{assignedToOtherDiet ? ' (assigned)' : ''}
                            </option>
                          );
                        })}
                      </select>
                      <div className="mt-2">
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => {
                            const target = selectedMenuId ? `/setup/menus/${selectedMenuId}/manage` : '/setup/menus/new/manage';
                            navigate(target);
                            onClose();
                          }}
                        >
                          Manage
                        </Button>
                      </div>

                    </div>
                    <div>
                      <label htmlFor="periodWeeks" className="form-label">Period (Weeks)</label>
                      <input
                        type="number"
                        className="form-input"
                        placeholder="e.g., 4"
                        {...register('periodWeeks', { valueAsNumber: true, min: { value: 1, message: 'Must be >= 1' } })}
                      />
                      {(errors as any)?.periodWeeks && (
                        <p className="form-error">{(errors as any).periodWeeks.message as string}</p>
                      )}
                    </div>
                    <div>
                      <label htmlFor="startingDay" className="form-label">Starting Day</label>
                      <select className="form-input" {...register('startingDay')}>
                        <option value="">Select day</option>
                        <option value="MONDAY">Monday</option>
                        <option value="TUESDAY">Tuesday</option>
                        <option value="WEDNESDAY">Wednesday</option>
                        <option value="THURSDAY">Thursday</option>
                        <option value="FRIDAY">Friday</option>
                        <option value="SATURDAY">Saturday</option>
                        <option value="SUNDAY">Sunday</option>
                      </select>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div>
                      <label htmlFor="startingDate" className="form-label">Starting Date</label>
                      <input type="date" className="form-input" {...register('startingDate')} />
                    </div>
                    <div>
                      <label className="form-label">Status</label>
                      <select
                        {...register('status')}
                        className="form-input"
                      >
                        <option value="ACTIVE">Active</option>
                        <option value="INACTIVE">Inactive</option>
                      </select>
                    </div>
                    <div>
                      <label className="form-label">Type</label>
                      <div className="border rounded max-h-[200px] overflow-y-auto p-2">
                        {DIET_TYPE_OPTIONS.map((opt) => {
                          const current = (watch('types') || []) as DietType[];
                          const checked = current.includes(opt.value);
                          return (
                            <label key={opt.value} className="flex items-center space-x-2 py-1">
                              <input
                                type="checkbox"
                                className="form-checkbox"
                                checked={checked}
                                onChange={(e) => {
                                  const next = e.target.checked
                                    ? [...current, opt.value]
                                    : current.filter((v) => v !== opt.value);
                                  setValue('types', next, { shouldDirty: true });
                                }}
                              />
                              <span className="text-sm text-gray-700">{opt.label}</span>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  </div>

                  {isEditing && diet?.statusDate && (
                    <div>
                      <label className="form-label">Status Date</label>
                      <input type="text" className="form-input" disabled value={new Date(diet.statusDate).toLocaleString()} />
                    </div>
                  )}

                  <div className="flex justify-end space-x-3 pt-4">
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={onClose}
                      disabled={isLoading}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      loading={isLoading}
                    >
                      {isEditing ? 'Update' : 'Create'}
                    </Button>
                  </div>
                </form>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default DietModal;
